import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { ArrowLeft, CheckCircle2, PartyPopper } from "lucide-react";
import { api, errMsg, emptyForm, toPayload, isPastSlot } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { BigButton } from "@/components/Layout";
import { ScrapPicker } from "@/components/ScrapPicker";
import { AreaField } from "@/components/Fields";
import { AddressChooser, NewAddress, pickAddress } from "@/components/AddressChooser";
import { SlotPicker } from "@/components/SlotPicker";
import { PaymentFields } from "@/components/PaymentFields";
import { PhotoInput } from "@/components/PhotoInput";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PrepTips, RepeatPicker } from "@/components/PickupExtras";
import { BookingSummary } from "@/components/BookingSummary";
import { TextField } from "@/components/Fields";
import { LoginPanel } from "@/components/AuthPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { mobileInput, mobileProps, MobileHint } from "@/lib/india";

const TITLES = { what: "What do you want to give?", where: "Where should we come?", when: "When should we come?",
  payment: "How do you want to be paid?", extras: "Anything else?", details: "Your details", review: "Check your pickup" };
const EDIT_KEYS = ["what", "where", "when", "payment", "extras"];
const DRAFT = "ks_draft";

const detailsError = (d) => {
  if (!d.customer_type) return "Please choose who you are.";
  if (!d.full_name.trim()) return "Please enter your full name.";
  if (d.customer_type !== "Household" && !d.business_name.trim()) return "Please enter your business name.";
  if (d.customer_type !== "Household" && !d.contact_person.trim()) return "Please enter the contact person's name.";
  if (!/^[6-9]\d{9}$/.test(d.mobile.replace(/\D/g, "").slice(-10))) return "Please enter a 10-digit mobile number.";
  return null;
};

const DetailsStep = ({ d, setD }) => {
  const { t, meta } = useApp();
  const set = (p) => setD((x) => ({ ...x, ...p }));
  const isBiz = d.customer_type && d.customer_type !== "Household";
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        {(meta?.customer_types || []).map((ct) => (
          <button key={ct} type="button" onClick={() => set({ customer_type: ct })} data-testid={`ctype-${ct.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`h-12 rounded-2xl border-2 text-sm font-semibold transition-colors ${d.customer_type === ct ? "border-[#2E9E6B] bg-[#2E9E6B]/10 text-[#0D3B2B]" : "border-[#D2E0D8] bg-white text-[#4A6358]"}`}>{t(ct)}</button>
        ))}
      </div>
      <TextField label={t("Full name")} testid="full-name-input" value={d.full_name} onChange={(e) => set({ full_name: e.target.value })} />
      {isBiz && <TextField label={t("Business name")} testid="business-name-input" value={d.business_name} onChange={(e) => set({ business_name: e.target.value })} />}
      {isBiz && <TextField label={t("Contact person")} testid="contact-person-input" value={d.contact_person} onChange={(e) => set({ contact_person: e.target.value })} />}
      <TextField label={t("Mobile number")} testid="mobile-input" {...mobileProps} placeholder="98290 12345" value={d.mobile} onChange={(e) => set({ mobile: mobileInput(e.target.value) })} />
      <MobileHint value={d.mobile} />
      <TextField label={t("Area / locality")} testid="area-input" placeholder={t("e.g. C-Scheme, Malviya Nagar")} hint={t("Shown on the leaderboard instead of your address.")}
        value={d.area} onChange={(e) => set({ area: e.target.value })} />
    </div>
  );
};

const stepError = (k, f, saved) => {
  const i = EDIT_KEYS.indexOf(k);
  if (i === 0 && !f.scrap_types.length) return "Please choose at least one scrap type.";
  if (i === 0 && f.scrap_types.includes("Other") && !f.other_text.trim()) return "You chose 'Other'. Please tell us what kind of scrap it is.";
  if (i === 1 && f.lat == null) return "Please set your location on the map.";
  if (i === 1 && !f.address.trim()) return "Please enter the pickup address.";
  if (i === 2 && !f.slot) return "Please pick a time slot.";
  if (i === 2 && isPastSlot(f.date, f.slot)) return "This time has already passed. Please choose a later time slot.";
  const keep = saved && saved.method === f.payment_method;
  if (i === 3 && f.payment_method === "UPI" && !f.upi_id && !(keep && saved.upi_masked)) return "Please enter your UPI ID, for example name@okaxis.";
  if (i === 3 && f.payment_method === "Bank" && !(keep && saved.account_last4) && (!f.bank_holder || !f.bank_account || !f.bank_ifsc))
    return "Please fill account holder name, account number and IFSC.";
  if (i === 3 && f.payment_method === "UPI" && f.upi_id && !/^[\w.-]{2,}@[A-Za-z][A-Za-z0-9]+$/.test(f.upi_id))
    return "That UPI ID doesn't look right. It should look like name@okaxis.";
  if (i === 3 && f.payment_method === "Bank" && f.bank_account && !/^\d{9,18}$/.test(f.bank_account))
    return "Account number should be 9 to 18 digits, numbers only.";
  if (i === 3 && f.payment_method === "Bank" && f.bank_ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(f.bank_ifsc))
    return "IFSC code should be 11 characters, like SBIN0001234.";
  return null;
};

export const Success = ({ booking }) => {
  const { t } = useApp();
  const nav = useNavigate();
  useEffect(() => {
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors: ["#2E9E6B", "#E05C3A", "#0D3B2B", "#FFD166"] });
  }, []);
  return (
    <div className="ks-rise flex flex-col items-center pt-8 text-center" data-testid="booking-success">
      <div className="mb-5 flex h-24 w-24 items-center justify-center rounded-full bg-[#2E9E6B]/15">
        <CheckCircle2 className="h-14 w-14 text-[#2E9E6B]" />
      </div>
      <h1 className="font-display text-3xl font-extrabold text-[#0D3B2B]">{t("Pickup booked!")}</h1>
      <p className="mt-2 text-[#4A6358]">{t("Thank you for choosing to recycle. You're helping keep Jaipur clean.")}</p>
      <div className="mt-6 w-full rounded-3xl border-2 border-dashed border-[#2E9E6B] bg-white p-5">
        <p className="text-xs font-bold uppercase tracking-widest text-[#7B968B]">{t("Booking ID")}</p>
        <p className="font-display text-3xl font-extrabold tracking-wide text-[#0D3B2B]" data-testid="success-booking-id">{booking.booking_id}</p>
        <p className="mt-2 font-semibold text-[#0D3B2B]" data-testid="success-booking-when">{booking.date} · {booking.slot_label}</p>
      </div>
      <p className="mt-4 flex items-center gap-2 text-sm text-[#4A6358]"><PartyPopper className="h-4 w-4 text-[#E05C3A]" />{t("We'll notify you when the team confirms.")}</p>
      <div className="mt-8 w-full space-y-3">
        <BigButton onClick={() => nav(`/history/${booking.booking_id}`)} data-testid="success-view-booking-btn">{t("View booking")}</BigButton>
        <button onClick={() => nav("/")} className="h-12 w-full font-semibold text-[#2E9E6B]" data-testid="success-home-btn">{t("Back to home")}</button>
      </div>
    </div>
  );
};

const readDraft = () => {
  try { return JSON.parse(localStorage.getItem(DRAFT) || "null"); } catch { return null; }
};

export default function Book() {
  const { user, setUser, t, refreshSummary, meta } = useApp();
  const draft = useRef(readDraft());
  const fromDraft = useRef(!!draft.current);
  const [addrs, setAddrs] = useState(null);
  const [key, setKey] = useState(draft.current ? "review" : "what");
  const [form, setForm] = useState(() => draft.current?.form || emptyForm(user || null));
  const [details, setDetails] = useState(() => draft.current?.details || {
    customer_type: user?.customer_type || "", full_name: user?.full_name || user?.name || "", business_name: user?.business_name || "",
    contact_person: user?.contact_person || "", mobile: user?.mobile || "", area: user?.area || "" });
  const [saved, setSaved] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [sameDay, setSameDay] = useState(null);
  const nav = useNavigate();
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const needDetails = !user || !user.onboarded;
  const skipWhere = addrs?.length === 1 && form.address_id && form.address_id !== "new" && key !== "where";
  const keys = [...EDIT_KEYS.filter((k) => !(skipWhere && k === "where")), ...(needDetails ? ["details"] : []), "review"];
  const step = Math.max(0, keys.indexOf(key));

  useEffect(() => {
    if (draft.current) {
      localStorage.removeItem(DRAFT);
      if (user) setConfirm(true);
      draft.current = null;
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!user) return;
    api.get("/settings").then(({ data }) => {
      setSaved(data.payment);
      if (data.payment?.method && !form.upi_id && !form.bank_account) set({ payment_method: data.payment.method });
    }).catch(() => {});
  }, [user?.user_id]); // eslint-disable-line

  useEffect(() => {
    if (!user) return;
    api.get("/addresses").then(({ data }) => {
      setAddrs(data);
      const d = data.find((a) => a.is_default);
      if (d && !fromDraft.current) set(pickAddress(d));
    }).catch(() => setAddrs([]));
  }, [user?.user_id]); // eslint-disable-line

  const go = (k) => { setKey(k); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const next = () => {
    const err = key === "details" ? detailsError(details) : stepError(key, form, saved);
    if (err) return toast.error(t(err));
    if (key === "review") return user ? setConfirm(true) : setLoginOpen(true);
    go(keys[step + 1]);
  };

  const saveDraft = () => {
    try { localStorage.setItem(DRAFT, JSON.stringify({ form, details })); }
    catch { localStorage.setItem(DRAFT, JSON.stringify({ form: { ...form, photo_data: "" }, details })); }
  };

  const uploadPhoto = async () => {
    if (!form.photo_data) return form.photo_path;
    try {
      const fd = new FormData();
      fd.append("file", await (await fetch(form.photo_data)).blob(), "photo.jpg");
      return (await api.post("/upload", fd)).data.path;
    } catch {
      toast.error(t("The photo couldn't be uploaded, so the pickup is booked without it."));
      return "";
    }
  };

  const submit = async (force = false) => {
    setSameDay(null);
    setBusy(true);
    try {
      if (!user.onboarded) {
        const { data } = await api.put("/profile", { ...details, alt_mobile: "", contact_email: user.email || "",
          address: form.address, landmark: form.landmark, lat: form.lat, lng: form.lng });
        setUser(data);
      }
      const photo_path = await uploadPhoto();
      const { photo_data, address_id, ...rest } = form;
      if (photo_path !== form.photo_path) set({ photo_path, photo_data: "" });
      const isNew = !address_id || address_id === "new";
      const { data } = await api.post("/bookings", toPayload({ ...rest, photo_path, confirm_same_day: force,
        save_address: isNew && form.save_address !== false, address_label: form.address_label || (addrs?.length ? "Other" : "Home") }));
      setDone(data);
      refreshSummary();
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.detail?.code === "same_day") setSameDay(e.response.data.detail);
      else toast.error(errMsg(e));
    } finally {
      setConfirm(false);
      setBusy(false);
    }
  };

  if (done) return <Success booking={done} />;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        {step > 0 && (
          <button onClick={() => go(keys[step - 1])} data-testid="book-back-btn" className="rounded-full bg-white p-2 shadow-sm"><ArrowLeft className="h-5 w-5" /></button>
        )}
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#2E9E6B]" data-testid="book-step-label">{t("Step")} {step + 1} / {keys.length}</p>
          <h1 className="font-display text-2xl font-extrabold text-[#0D3B2B]">{t(TITLES[key])}</h1>
        </div>
      </div>
      <div className="flex gap-1.5" data-testid="book-progress">
        {keys.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#2E9E6B]" : "bg-[#D2E0D8]"}`} />)}
      </div>

      <div key={key} className="ks-rise">
        {key === "what" && <ScrapPicker form={form} set={set} />}
        {key === "where" && (
          addrs?.length ? <AddressChooser addrs={addrs} form={form} set={set} /> : <NewAddress form={form} set={set} canSave={!!user && !needDetails} />
        )}
        {key === "when" && <div className="space-y-5"><SlotPicker form={form} set={set} /><RepeatPicker value={form.repeat || ""} onChange={(repeat) => set({ repeat })} /></div>}
        {key === "payment" && <PaymentFields form={form} set={set} saved={saved} />}
        {key === "extras" && (
          <div className="space-y-4">
            <PrepTips types={form.scrap_types} />
            <AreaField label={t("Note for the pickup team (optional)")} testid="note-input" placeholder={t("e.g. Call when you reach the gate")}
              value={form.note} onChange={(e) => set({ note: e.target.value })} />
            <PhotoInput value={form.photo_path} data={form.photo_data} guest={!user} onData={(d) => set({ photo_data: d })}
              onChange={(p) => set({ photo_path: p, photo_data: "" })} testid="booking-photo" />
          </div>
        )}
        {key === "details" && <DetailsStep d={details} setD={setDetails} />}
        {key === "review" && addrs?.length === 1 && form.address_id && form.address_id !== "new" && (
          <p className="mb-3 rounded-full bg-[#2E9E6B]/10 px-4 py-2 text-xs font-semibold text-[#0D3B2B]" data-testid="using-saved-address">{t("Using your saved address. Tap Edit to change it.")}</p>
        )}
        {key === "review" && <BookingSummary b={form} saved={saved} onEdit={(i) => go(EDIT_KEYS[i])} />}
      </div>

      <BigButton coral={key === "review"} onClick={next} data-testid="book-next-btn">
        {key === "review" ? t("Confirm pickup") : t("Next")}
      </BigButton>
      {key === "review" && !user && <p className="text-center text-sm text-[#4A6358]">{t("Last step: log in with Google or your mobile number to confirm.")}</p>}

      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent className="max-w-[92vw] rounded-3xl sm:max-w-sm" data-testid="booking-login-dialog">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-xl text-[#0D3B2B]">{t("Log in to confirm your pickup")}</DialogTitle>
            <DialogDescription>{t("Your booking details are kept. It takes a few seconds.")}</DialogDescription>
          </DialogHeader>
          <LoginPanel afterPath="/book" beforeGoogle={saveDraft} defaultMobile={details.mobile}
            onDone={() => { setLoginOpen(false); setConfirm(true); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!sameDay} onOpenChange={(o) => !o && setSameDay(null)}>
        <DialogContent className="max-w-[92vw] rounded-3xl sm:max-w-sm" data-testid="same-day-dialog">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-xl text-[#0D3B2B]">{t("You already have a pickup on this date")}</DialogTitle>
            <DialogDescription data-testid="same-day-message">
              {t("Pending pickup")}: {sameDay?.booking_id} · {sameDay?.slot_label}. {t("Are you sure you want to book another pickup for the same date?")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <BigButton coral onClick={() => submit(true)} data-testid="same-day-book-anyway-btn">{t("Yes, book another pickup")}</BigButton>
            <button onClick={() => { setSameDay(null); go("when"); }} data-testid="same-day-change-new-btn"
              className="h-12 w-full rounded-2xl border border-[#D2E0D8] font-semibold text-[#0D3B2B]">{t("Change time of this new pickup")}</button>
            <button onClick={() => nav(`/history/${sameDay.booking_id}/edit`)} data-testid="same-day-edit-existing-btn"
              className="h-12 w-full rounded-2xl border border-[#D2E0D8] font-semibold text-[#0D3B2B]">{t("Change time of existing pickup")}</button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title={t("Confirm this pickup?")} busy={busy} onYes={() => submit()}
        lines={[`${form.date} · ${meta?.slots?.find((s) => s.value === form.slot)?.label || ""}`, form.scrap_types.map(t).join(", ")]} />
    </div>
  );
}
